import { Inject, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Router, RouterStateSnapshot } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { Observable, throwError } from 'rxjs';
import { tap } from "rxjs/operators";
import { AuthorizationService } from '../../services/authorization';
import { catchError, mergeMap } from 'rxjs/operators';
import { MoovTokenErrorResponse, IMoovTokenSuccessResponse } from '../../interfaces';
import { NAVIGATION_ROUTES } from '../../../navigation-routes.const';
import { LocalStorageService } from '../../services/localStorage';
import { LocalStorageKeys } from '../../services/localStorage/local-storage.enum';
import { ReadKeyExpr } from '@angular/compiler';
import config from 'src/app/config';
import { AbstractLocalStorageService } from '../../services/localStorage/local-storage.abstract.service';
import { AbstractAuthorizationService } from '../../services/authorization/authorization.abstract.service';
import { APIS } from 'src/app/apis';
import { AbstractPersonalUserDataService } from '../../services/personal-user-data/personal-user-data.abstract.service';
import { ResponsesService } from '../../services/responses/responses.service';

@Injectable(
  { providedIn: 'root', }
)
export class  HttpTokenInterceptorService implements HttpInterceptor {

  private token: string;
  private state: RouterStateSnapshot;
  
  constructor(private injector: Injector,
              @Inject(DOCUMENT) private document: Document,
              private router: Router,
              private localStorageService: AbstractLocalStorageService,
              private authorizationService: AbstractAuthorizationService,
              private personalUserDataService: AbstractPersonalUserDataService,
              private responsesService: ResponsesService
              
  ) {
    this.handleFetchTokenError = this.handleFetchTokenError.bind(this) as (err: MoovTokenErrorResponse) => Observable<MoovTokenErrorResponse>;
      this.state = router.routerState.snapshot;
  }

  intercept(request: HttpRequest<undefined>, next: HttpHandler): Observable<HttpEvent<undefined>> {
    return this.isUrlAllowedWithoutToken(request) ?
      this.continueRequestWithoutToken(request, next) :
      this.addTokenToRequest(request, next);
  }
  
  private getTranslationFilePath(url: string) {
    const translationPath = url.match(config.translationFilesRegEx);
    // console.log(`translationPath ${translationPath}`);
    return  translationPath ? translationPath[0] : "";
  }

  private isResetPasswordUrl(url: string):boolean {
    return url.includes(this.personalUserDataService.getResetUserPasswordUrl());
  }

  private isTokenLoginUrl(url:string): boolean{
    return url.includes(this.authorizationService.getTokenLoginUrl());
  }

  private isValidateTokenlessRegistrationForCustomerUrl(url:string): boolean{
    return url.includes(this.authorizationService.getValidateTokenlessRegistrationForCustomerUrl());
  }

  private isUnsubscribeUrl(url:string): boolean{
    return url.includes(this.personalUserDataService.getUnsubscribeUrl());
  }
  
  private isVerifyFeatureUrl(url:string): boolean{
    return url.includes(this.personalUserDataService.getVerifyFeatureUrl());
  }
  
  private isRegisterUserFromTokenUrl(url:string): boolean{
    // console.log(`httpTokenInterceptor isRegisterUserFromTokenUrl`);
    return url.includes(this.authorizationService.getRegisterUserFromTokenUrl())
  }

  private isSendUserResponseUrl(url:string): boolean{
    return url.includes(this.responsesService.getSendUserResponseUrl())
  }

  private isSendTipToSelfUrl(url:string): boolean{
    return url.includes(this.responsesService.getSendToSelfUrl())
  }

  private isGetPulseCheckRequiredDataUrl(url:string): boolean{
    return url.includes(this.responsesService.getPulseCheckRequiredDataUrl);
  }

  private isSendPulseResponseUrl_noAuth(url:string): boolean{
    return url.includes(this.responsesService.sendPulseResponse_noAuthUrl);
  }

  private isUrlAllowedWithoutToken(request: HttpRequest<undefined>): boolean {
    // console.log(`httpTokenInterceptor isUrlAllowedWithoutToken url is ${request.url}`);
    if  (this.isResetPasswordUrl(request.url)           || 
        (this.isTokenLoginUrl(request.url))             || 
        (this.isRegisterUserFromTokenUrl(request.url))  || 
        (this.isValidateTokenlessRegistrationForCustomerUrl(request.url)) ||
        (this.isSendUserResponseUrl(request.url))       ||
        (this.isSendTipToSelfUrl(request.url))          ||
        (this.isGetPulseCheckRequiredDataUrl(request.url)) ||
        (this.isSendPulseResponseUrl_noAuth(request.url))        ||
        (this.isUnsubscribeUrl(request.url))            ){
          // console.log(`httpTokenInterceptor isUrlAllowedWithoutToken url is ${request.url} and allowed`);
      return true;
    }
    
    // DEBUG 
    // if (request.url.includes('allMotivations')){
    //   return true;
    // }

    // console.log(`isUrlAllowedWithoutToken url is ${request.url}`);

    switch (request.url) {
      case this.getTranslationFilePath(request.url):{
        return true;
      }
      case this.authorizationService.getTokenUrl(): {
        return true;
      }
      case NAVIGATION_ROUTES.home:
        {
          return true;
        }
      case NAVIGATION_ROUTES.login:
        {
          return true;
        } 
      case NAVIGATION_ROUTES.tokenLogin:
        {
          return true;
        }
      case '/assets/i18n/en-US.json':
        {
          return true;
        }
      case '/assets/i18n/he-IL.json':
        {
          return true;
        }        
      case '/assets/common/config.json':
          {
            return true;
          }
      case '/assets/common/env.config.json':
        {
          return true;
        }
      case NAVIGATION_ROUTES.app.info: 
        {
          return true;
        }
      default: {
        // console.log(`httpTokenInterceptor isUrlAllowedWithoutToken switch failes - url is ${request.url} and not allowed`);
        return false;
      }
    }
  }

  private continueRequestWithoutToken(req: HttpRequest<undefined>, next: HttpHandler): Observable<HttpEvent<undefined>> {
    // this.fetchNewTokenAndAddToRequest(request, next);
    // console.log("httpTokenInterceptor continueRequestWithoutToken");
    return next.handle(req).pipe(
      catchError((err: MoovTokenErrorResponse): Observable<HttpEvent<undefined>> => this.handleHttpErrorResponse(err, req, next)));
  }

  private addTokenToRequest(request: HttpRequest<undefined>, next: HttpHandler): Observable<HttpEvent<undefined>> {
    // console.log(`httpTokenInterceptor addTokenToRequest url is ${request.url}`);

    const token: string = this.getToken();
    // return token ? this.validateTokenAndMakeRequest(request, next, token) : this.fetchNewTokenAndAddToRequest(request, next);
    return token ? this.validateTokenAndMakeRequest(request, next, token) : this.removeAndRedirectToLogin(request, next);
  }

  private getToken(): string {
    if (!this.token) {
      try {
        this.token = this.localStorageService.get<string>(LocalStorageKeys.moovToken);
      } catch (ex) {
        console.log(`HttpTokenInterceptorService failed to retreive token from local storage error is ${ex}`);
        return "";
      }
    }
    return this.token;
  }

  private validateTokenAndMakeRequest(request: HttpRequest<undefined>, next: HttpHandler, token: string): Observable<HttpEvent<undefined>> {
    // return this.isInvalidToken(token) ? this.removeAndFetchToken(request, next) : this.continueRequestWithToken(request, next, token);
    return this.isInvalidToken(token) ? this.removeAndRedirectToLogin(request, next) : this.continueRequestWithToken(request, next, token);
  }

  private isInvalidToken(token: string): boolean {
    return this.authorizationService.isTokenExpired(token);
  }
  
  private removeAndRedirectToLogin(request: HttpRequest<undefined>, next: HttpHandler): Observable<HttpEvent<undefined>> {
    // console.log('httpTokenINterceptor removeAndRedirectToLogin');
    this.resetTokenData();
    // // @ts-ignore
    // return this.authorizationService.getMoovToken("", "").pipe(
    //   catchError(this.handleFetchTokenError),
    //   // eslint-disabl-next-line
    //   mergeMap(this.handleFetchTokenSuccess.bind(this, request,next)),
    // );




    // 
    // // return 
    // // return next.handle(request).pipe((event: HttpEvent<any>) => {
    // //   this.router.navigateByUrl(NAVIGATION_ROUTES.login);
    // // });
    this.localStorageService.write<string>(LocalStorageKeys.returnUrl, this.state.url);
     return (Observable.create(
       (observer: any) => {
            observer.next(this.router.navigateByUrl(NAVIGATION_ROUTES.login));
        })
      );
}
  // private removeAndFetchToken(request: HttpRequest<undefined>, next: HttpHandler): Observable<HttpEvent<undefined>> {
  //   this.resetTokenData();
  //   return this.fetchNewTokenAndAddToRequest(request, next);
  // }

  private resetTokenData(): void {
    this.token = '';
    this.authorizationService.resetMoovToken();
  }

  // private fetchNewTokenAndAddToRequest(request: HttpRequest<undefined>, next: HttpHandler): Observable<HttpEvent<undefined>> {
  //   return this.authorizationService.fetchMoovToken().pipe(
  //     catchError(this.handleFetchTokenError),
  //     // eslint-disable-next-line
  //     mergeMap(this.handleFetchTokenSuccess.bind(this, request, next)),
  //   );
  // }

  private handleFetchTokenError(err: MoovTokenErrorResponse): Observable<MoovTokenErrorResponse> {
    if (err instanceof HttpErrorResponse && this.authorizationService.isSessionExpiredRequestError(err)) {
      // this.authorizationService.removeAttTokens();
      this.localStorageService.write<string>(LocalStorageKeys.returnUrl, this.state.url);
      this.router.navigateByUrl(NAVIGATION_ROUTES.login);
    }
    return throwError(err);
  }

  private handleFetchTokenSuccess(request: HttpRequest<undefined>, next: HttpHandler, res: IMoovTokenSuccessResponse): Observable<HttpEvent<undefined>> {
    this.token = res.token;
    this.localStorageService.write<string>(LocalStorageKeys.moovToken, this.token);
    this.authorizationService.saveUserDetailsLocally(this.token);
    return this.continueRequestWithToken(request, next, this.token);
  }

  private continueRequestWithToken(request: HttpRequest<undefined>, next: HttpHandler, token: string): Observable<HttpEvent<undefined>> {
    const req = this.getRequestWithHeaders(request, token);
    return next.handle(req).pipe(
      catchError((err: MoovTokenErrorResponse): Observable<HttpEvent<undefined>> => this.handleHttpErrorResponse(err, request, next)));
  }

  private getRequestWithHeaders(request: HttpRequest<undefined>, token: string): HttpRequest<undefined> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  handleHttpErrorResponse(err: HttpErrorResponse, request: HttpRequest<undefined>, next: HttpHandler): Observable<HttpEvent<undefined>> {
    //return Observable.throw(err);
     //throwError(err);
    return this.isMoovTokenExpired(err) ? this.fetchNewTokenAndRetryFetchRequestDataAgain(request, next) : throwError(err);
  }

  private isMoovTokenExpired(err: HttpErrorResponse): boolean {
    return err instanceof HttpErrorResponse && this.authorizationService.isMoovTokenSessionExpiredRequestError(err);
  }

  private fetchNewTokenAndRetryFetchRequestDataAgain(request: HttpRequest<undefined>, next: HttpHandler): Observable<HttpEvent<undefined>> {
    return this.removeAndRedirectToLogin(request, next);
  }
}
